import { Base64 } from 'js-base64';
import { AccountRecoveryEmailVerificationResponseDto, AccountRecoveryVerificationDto, InitiateAccountRecoveryDto, ScramServerFirstDto, UserCryptoDto, } from '@lucidlink/dto/web-service-api';
import { derivePasswordKey } from '@lucidlink/ts-crypto/crypto';
import { UnauthorizedError } from '#fetcher/fetch-error.types.ts';
import { parserFromDto } from '#fetcher/parse-utils.ts';
import { deriveUserIdentity, prepareClientFinal, prepareClientFirst, } from '../common';
export const createAccountRecoveryClient = ({ getUnauthenticatedFetcher, }) => {
    return new AccountRecoveryClientImpl(getUnauthenticatedFetcher);
};
export class AccountRecoveryClientImpl {
    getUnauthenticatedFetcher;
    email;
    accountRecoveryFetcher;
    accountCryptoParams;
    timeoutId;
    constructor(getUnauthenticatedFetcher) {
        this.getUnauthenticatedFetcher = getUnauthenticatedFetcher;
    }
    async sendVerificationCode(email) {
        await this.getUnauthenticatedFetcher().put(['recovery', 'init'], {
            body: InitiateAccountRecoveryDto.create({ email }),
        });
    }
    async verifyEmail(verifyEmailParams) {
        const { accessToken } = await this.getUnauthenticatedFetcher().put(['recovery', 'email-verification'], {
            body: AccountRecoveryVerificationDto.create(verifyEmailParams),
            parser: parserFromDto(AccountRecoveryEmailVerificationResponseDto),
        });
        this.resetState();
        this.startResetTimer(getTtl(accessToken));
        this.email = verifyEmailParams.email;
        this.accountRecoveryFetcher =
            this.getUnauthenticatedFetcher().createSubFetcher({
                getAccessToken: async (_) => Promise.resolve(accessToken),
            });
    }
    async verifyBackupCode(backupCode) {
        if (!this.email) {
            throw new Error('You need to verify email first');
        }
        const accountRecoveryFetcher = this.getAccountRecoveryFetcher();
        const clientFirst = prepareClientFirst(this.email);
        const serverFirst = await accountRecoveryFetcher
            .put(['recovery', 'backup-code-verification', 'first'], {
            body: clientFirst,
            parser: parserFromDto(ScramServerFirstDto),
        })
            .catch(this.unauthorizedHandler);
        const clientFinal = await prepareClientFinal(clientFirst, serverFirst, backupCode);
        const recoveryCrypto = await accountRecoveryFetcher
            .put(['recovery', 'backup-code-verification', 'final'], {
            body: clientFinal,
            parser: parserFromDto(UserCryptoDto),
        })
            .catch(this.unauthorizedHandler);
        this.accountCryptoParams = {
            eAccountKey: recoveryCrypto.userCryptoKey,
            wrappingKey: await derivePasswordKey(backupCode, recoveryCrypto.userCryptoKeyIterations, recoveryCrypto.userCryptoKeySalt),
        };
    }
    async resetPassword(resetPasswordParams) {
        const accountRecoveryFetcher = this.getAccountRecoveryFetcher();
        if (!this.accountCryptoParams) {
            throw new Error('You need to verify backup code first');
        }
        const userIdentity = await deriveUserIdentity(resetPasswordParams.newPassword, this.accountCryptoParams);
        await accountRecoveryFetcher
            .put(['recovery', 'restore'], {
            body: userIdentity,
            queryParams: { reset2FA: resetPasswordParams.reset2FA },
        })
            .finally(() => {
            this.resetState();
        });
    }
    getAccountRecoveryFetcher = () => {
        if (!this.accountRecoveryFetcher) {
            throw new Error('You need to verify email first');
        }
        return this.accountRecoveryFetcher;
    };
    unauthorizedHandler = (error) => {
        if (error instanceof UnauthorizedError) {
            // token has expired
            this.resetState();
        }
        throw error;
    };
    clearResetTimer = () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
    };
    startResetTimer = (ms) => {
        this.timeoutId = setTimeout(() => {
            this.resetState();
        }, ms);
    };
    resetState = () => {
        this.email = undefined;
        this.accountRecoveryFetcher = undefined;
        this.accountCryptoParams = undefined;
        this.clearResetTimer();
    };
}
const decodeJwt = (token) => {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) {
        throw Error('Cannot parse token');
    }
    const payload = Base64.decode(payloadB64);
    return JSON.parse(payload);
};
const getTtl = (token) => {
    const jwt = decodeJwt(token);
    return (jwt.exp - jwt.iat) * 1000; // milliseconds
};
